import { createFileRoute } from "@tanstack/react-router";
import {
  Building2,
  ChevronRight,
  FileWarning,
  Info,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
  EditarCuenta,
  EditarEmpresa,
  useReactivar,
  useRetirar,
} from "@/components/maestros/editar";
import { Cabecera } from "@/components/propuesta/cabecera";
import { Aviso, Estado, Pista, Tile } from "@/components/propuesta/piezas";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/empresas")({ component: Maestro });

// ===========================================================================
// El maestro de empresas y cuentas.
//
// Contesta dos preguntas que conviene no mezclar:
//
//   · «¿de quién es esta cuenta?» — el maestro, que está completo: cada cuenta
//     de depósito pertenece a un RUC, que es la regla que fijó contabilidad y
//     lo que permite decir «este abono es de EcoPlaza Mercado» en vez de «este
//     abono es de alguien».
//
//   · «¿de qué cuentas tenemos extracto?» — la cobertura, que NO está
//     completa. Y esa es la que hay que ver antes de cerrar un mes.
//
// El buscador es el de la mesa: por nombre, por RUC, por número entero y por
// los últimos dígitos. Lo último es lo que hace falta de verdad, porque los
// vouchers escriben «**** 2100» y desde ahí hay que poder llegar a la empresa.
// ===========================================================================

const ESTADO = {
  con_movimientos: { etiqueta: "con movimientos", tono: "ok" },
  extracto_vacio: { etiqueta: "extracto sin líneas", tono: "warn" },
  sin_extracto: { etiqueta: "sin extracto", tono: "neutro" },
} as const satisfies Record<
  string,
  { etiqueta: string; tono: "ok" | "warn" | "neutro" }
>;

/** El estado no llega tipado desde el contrato: se resuelve con red de seguridad. */
function comoEsta(estado: string) {
  return ESTADO[estado as keyof typeof ESTADO] ?? ESTADO.sin_extracto;
}

function Maestro() {
  const [altaEmpresa, setAltaEmpresa] = useState(false);
  const [buscar, setBuscar] = useState("");

  // Se busca en el servidor, que es donde está el maestro entero: así el
  // filtro por final de cuenta usa el mismo normalizador que el cruce, y no
  // una segunda versión en el navegador que se desvíe de la primera.
  const { data, isPending } = api.maestros.useQuery(
    ["maestros", buscar],
    { query: buscar ? { buscar } : {} },
    { staleTime: 30_000, placeholderData: (previo: unknown) => previo },
  );
  const d = data?.status === 200 ? data.body : null;

  // Un resultado corto se abre solo: si buscaste «**** 2100» querés ver la
  // cuenta, no un nombre plegado. Cincuenta empresas abiertas de golpe, en
  // cambio, es una parrafada por la que hay que hacer scroll.
  const abrirSolas = !!buscar && !!d && d.mostradas <= 8;

  const sinExtracto = d ? d.cuentas - d.cuentasConExtracto : 0;
  const cobertura = d?.cuentas ? (d.cuentasConExtracto / d.cuentas) * 100 : 0;

  return (
    <>
      <Cabecera
        migas={["Maestros"]}
        titulo="Empresas y cuentas"
        acciones={
          <Button size="sm" onClick={() => setAltaEmpresa(true)}>
            <Plus /> Nueva razón social
          </Button>
        }
        buscar={buscar}
        alBuscar={setBuscar}
        marcador="Razón social, RUC, número de cuenta o sus últimos dígitos…"
        menus={[]}
        facetas={
          buscar && d
            ? [
                {
                  clave: "buscar",
                  valor: `${d.mostradas} de ${d.empresas}`,
                  alQuitar: () => setBuscar(""),
                },
              ]
            : []
        }
      />

      <p className="-mt-2 max-w-3xl text-[13px] text-muted-foreground">
        Cada cuenta de depósito pertenece a una razón social. Es lo que decide a
        qué empresa se imputa cada abono: sin eso, un pago puede cruzarse con la
        boleta de otra empresa del grupo y el reporte sale mal sin que nada
        avise.
      </p>

      {isPending && !d ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {["a", "b", "c", "d"].map((k) => (
            <Skeleton key={k} className="h-[104px]" />
          ))}
        </div>
      ) : d ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile
            titulo="Empresas"
            subtitulo="razones sociales del grupo"
            valor={d.empresas.toLocaleString("es-PE")}
            tono="acc"
            pie={
              <span>
                {d.empresasConMovimientos === 1
                  ? "1 con movimientos cargados"
                  : `${d.empresasConMovimientos} con movimientos cargados`}
                {d.empresasSinCuenta > 0
                  ? ` · ${d.empresasSinCuenta} sin ninguna cuenta`
                  : ""}
              </span>
            }
          />
          <Tile
            titulo="Cuentas bancarias"
            subtitulo="en los cuatro bancos del maestro"
            valor={d.cuentas.toLocaleString("es-PE")}
            tono="acc"
            pie={
              <span>
                {d.cuentasSinCci > 0
                  ? `${d.cuentasSinCci} sin CCI`
                  : "todas con CCI"}
              </span>
            }
          />
          <Tile
            titulo="Con extracto cargado"
            subtitulo="de cuántas sabemos qué pasó"
            valor={d.cuentasConExtracto.toLocaleString("es-PE")}
            unidad={`/ ${d.cuentas}`}
            tono={cobertura < 50 ? "warn" : "ok"}
          >
            <Pista
              porcentaje={cobertura}
              tono={cobertura < 50 ? "warn" : "ok"}
            />
          </Tile>
          <Tile
            titulo="Por banco"
            subtitulo="cuentas · con extracto"
            tono="neutro"
          >
            <div className="mt-2 space-y-1">
              {d.porBanco.map((b: (typeof d.porBanco)[number]) => (
                <div
                  key={b.banco}
                  className="flex items-baseline justify-between text-[11.5px]"
                >
                  <span className="text-muted-foreground">{b.banco}</span>
                  <span className="tabular-nums">
                    <b className="font-semibold">{b.cuentas}</b>
                    <span className="text-muted-foreground">
                      {" · "}
                      {b.conExtracto}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </Tile>
        </div>
      ) : null}

      {d && sinExtracto > 0 ? (
        <Aviso tono="info">
          <Info className="mt-px size-4 shrink-0" />
          <span>
            <b>
              {sinExtracto} de las {d.cuentas} cuentas no tienen ningún extracto
              cargado.
            </b>{" "}
            No están mal dadas de alta: es que nadie ha entregado todavía su
            estado de cuenta. Mientras sigan así, lo que haya pasado en ellas no
            entra en el cierre y ningún total del grupo las incluye.
          </span>
        </Aviso>
      ) : null}

      {isPending && !d ? (
        <div className="space-y-2">
          {["a", "b", "c", "d", "e"].map((k) => (
            <Skeleton key={k} className="h-14" />
          ))}
        </div>
      ) : !d || d.filas.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FileWarning className="mx-auto size-9 text-muted-foreground/40" />
            <p className="mt-3 font-medium">
              Ninguna empresa ni cuenta coincide con «{buscar}»
            </p>
            <p className="mt-1 text-muted-foreground text-sm">
              Se busca por razón social, RUC, número de cuenta, CCI o sus
              últimos dígitos —al menos tres.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {d.filas.map((e: (typeof d.filas)[number]) => (
            // La clave lleva el modo para que la tarjeta se vuelva a montar
            // cuando cambia: el estado inicial de un `useState` solo se aplica
            // al montar, y sin esto el resultado de «**** 2100» salía plegado
            // y había que abrirlo a mano.
            <Empresa
              key={`${e.id}${abrirSolas ? "·abierta" : ""}`}
              empresa={e}
              abiertaPorDefecto={abrirSolas}
            />
          ))}
        </div>
      )}

      {altaEmpresa ? (
        <EditarEmpresa empresa={null} alCerrar={() => setAltaEmpresa(false)} />
      ) : null}
    </>
  );
}

type FilaEmpresa = {
  id: string;
  ruc: string;
  razonSocial: string;
  activa: boolean;
  movimientos: number;
  cuentas: Array<{
    id: string;
    banco: string;
    numero: string;
    moneda: "PEN" | "USD";
    cci: string | null;
    alias: string | null;
    movimientos: number;
    extractos: number;
    entregaDesde: string | null;
    entregaHasta: string | null;
    activa: boolean;
    meses: string[];
    huecos: string[];
    estado: string;
    coincide: boolean;
  }>;
};

/**
 * Una empresa, plegada. Lo que se lee sin abrir —RUC, cuántas cuentas, en qué
 * bancos y si tiene movimientos— es lo que se consulta a diario; el detalle de
 * cada número queda a un clic, que es lo que se mira una vez.
 */
function Empresa({
  empresa,
  abiertaPorDefecto,
}: {
  empresa: FilaEmpresa;
  abiertaPorDefecto: boolean;
}) {
  const [abierta, setAbierta] = useState(abiertaPorDefecto);
  // Qué se está editando, si algo. El estado vive en la tarjeta y no en la
  // página: cada empresa abre lo suyo y no hay que arrastrar el id hacia
  // arriba solo para saber a cuál pertenece la cuenta nueva.
  const [editando, setEditando] = useState<
    | { clase: "empresa" }
    | {
        clase: "cuenta";
        cuenta: FilaEmpresa["cuentas"][number] | null;
        empresaId: string;
      }
    | null
  >(null);
  const { retirarEmpresa, retirarCuenta } = useRetirar();
  const { reactivarEmpresa, reactivarCuenta } = useReactivar();

  const bancos = useMemo(() => {
    const vistos: string[] = [];
    for (const c of empresa.cuentas)
      if (!vistos.includes(c.banco)) vistos.push(c.banco);
    return vistos;
  }, [empresa.cuentas]);

  const conExtracto = empresa.cuentas.filter((c) => c.extractos > 0).length;
  const coinciden = empresa.cuentas.filter((c) => c.coincide).length;

  return (
    <Card className="overflow-hidden py-0">
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40"
      >
        <ChevronRight
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            abierta && "rotate-90",
          )}
        />
        <Building2 className="size-4 shrink-0 text-muted-foreground" />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-sm">
              {empresa.razonSocial}
            </span>
            {empresa.activa ? null : <Estado tono="neutro">inactiva</Estado>}
          </div>
          <div className="mt-0.5 text-[11.5px] text-muted-foreground tabular-nums">
            RUC {empresa.ruc}
            {bancos.length ? ` · ${bancos.join(", ")}` : ""}
            {coinciden > 0 ? (
              <span className="ml-1 font-medium text-cyan-700 dark:text-cyan-400">
                ·{" "}
                {coinciden === 1
                  ? "1 cuenta coincide"
                  : `${coinciden} cuentas coinciden`}
              </span>
            ) : null}
          </div>
        </div>

        <div className="shrink-0 text-right text-[11.5px] text-muted-foreground">
          <div className="tabular-nums">
            {empresa.cuentas.length === 0 ? (
              "sin cuentas"
            ) : (
              <>
                <b className="font-semibold text-foreground">
                  {empresa.cuentas.length}
                </b>{" "}
                {empresa.cuentas.length === 1 ? "cuenta" : "cuentas"}
                {conExtracto > 0 ? ` · ${conExtracto} con extracto` : ""}
              </>
            )}
          </div>
          {empresa.movimientos > 0 ? (
            <div className="tabular-nums">
              {empresa.movimientos.toLocaleString("es-PE")} movimientos
            </div>
          ) : null}
        </div>
      </button>

      {abierta ? (
        <div className="border-t bg-muted/20">
          {/* Qué se puede hacer con esta razón social. «Retirar» no promete
              borrar: el servidor decide entre borrar y dar de baja según lo que
              cuelgue, y lo dice al responder. */}
          <div className="flex flex-wrap items-center gap-2 border-b bg-background px-4 py-2.5">
            {!empresa.activa ? (
              <Estado tono="neutro">dada de baja</Estado>
            ) : null}
            {/* Agregar es lo que se viene a buscar, así que va rellena;
                corregir es frecuente y no destaca; retirar va en rojo porque es
                la única que quita algo. Antes eran tres enlaces grises iguales
                y no se distinguía la que borra de las que no. */}
            <Button
              size="xs"
              onClick={() =>
                setEditando({
                  clase: "cuenta",
                  cuenta: null,
                  empresaId: empresa.id,
                })
              }
            >
              <Plus /> Agregar cuenta
            </Button>
            <Button
              size="xs"
              variant="outline"
              onClick={() => setEditando({ clase: "empresa" })}
            >
              <Pencil /> Corregir
            </Button>
            {empresa.activa ? (
              <Button
                size="xs"
                variant="destructive"
                onClick={() => retirarEmpresa(empresa.id)}
              >
                <Trash2 /> Retirar
              </Button>
            ) : (
              <Button
                size="xs"
                variant="outline"
                className="border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400"
                onClick={() => reactivarEmpresa(empresa.id)}
              >
                <RotateCcw /> Reactivar
              </Button>
            )}
            <span className="ml-auto text-[11px] text-muted-foreground">
              Retirar no borra si hay historial: da de baja y lo conserva.
            </span>
          </div>
          {empresa.cuentas.length === 0 ? (
            <p className="px-4 py-4 text-[12.5px] text-muted-foreground">
              Esta empresa está en el maestro sin ninguna cuenta. No es un error
              de carga: en el Excel tampoco tiene ninguna.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="border-b text-[11px] text-muted-foreground uppercase tracking-wide">
                  <tr>
                    <th className="py-2 pl-11 text-left font-medium">Banco</th>
                    <th className="py-2 text-left font-medium">Cuenta</th>
                    <th className="py-2 pl-6 text-left font-medium">Moneda</th>
                    <th className="py-2 pl-6 text-left font-medium">CCI</th>
                    <th className="py-2 pl-6 text-right font-medium">
                      Movimientos
                    </th>
                    <th className="py-2 pl-6 text-left font-medium">
                      Entrega extracto
                    </th>
                    <th className="py-2 pl-6 text-left font-medium">
                      Cobertura
                    </th>
                    <th className="py-2 pr-4 pl-6 text-right font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {empresa.cuentas.map((c) => {
                    const e = comoEsta(c.estado);
                    return (
                      <tr
                        key={c.id}
                        className={cn(
                          "border-b last:border-0",
                          // La que hizo entrar a la empresa en el filtro. Sin
                          // esto, buscar «**** 2100» devuelve ocho cuentas y
                          // hay que compararlas a ojo una por una.
                          c.coincide && "bg-cyan-500/10",
                        )}
                      >
                        <td className="py-2 pl-11 font-medium">{c.banco}</td>
                        <td className="py-2 font-mono tabular-nums">
                          {c.numero}
                          {c.alias ? (
                            <span className="ml-2 font-sans text-muted-foreground">
                              {c.alias}
                            </span>
                          ) : null}
                        </td>
                        <td className="py-2 pl-6 tabular-nums">{c.moneda}</td>
                        <td className="py-2 pl-6 font-mono text-muted-foreground tabular-nums">
                          {c.cci ?? "—"}
                        </td>
                        <td className="py-2 pl-6 text-right tabular-nums">
                          {c.movimientos
                            ? c.movimientos.toLocaleString("es-PE")
                            : "—"}
                        </td>
                        {/* Desde y hasta qué mes se le pide extracto. Es lo
                            que separa «esta cuenta no entregó» de «esta cuenta
                            no existía», y lo que hace que el checklist del mes
                            pida lo que toca y no las 154 del maestro. */}
                        <td className="py-2 pl-6">
                          <RangoDeEntrega cuenta={c} />
                        </td>
                        <td className="py-2 pl-6">
                          {c.activa ? (
                            <Estado tono={e.tono}>{e.etiqueta}</Estado>
                          ) : (
                            <Estado tono="neutro">dada de baja</Estado>
                          )}
                        </td>
                        <td className="whitespace-nowrap py-2 pr-4 pl-6 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() =>
                                setEditando({
                                  clase: "cuenta",
                                  cuenta: c,
                                  empresaId: empresa.id,
                                })
                              }
                            >
                              <Pencil /> Corregir
                            </Button>
                            {c.activa ? (
                              <Button
                                size="xs"
                                variant="destructive"
                                title="Retirar del maestro"
                                onClick={() => retirarCuenta(c.id)}
                              >
                                <Trash2 /> Retirar
                              </Button>
                            ) : (
                              <Button
                                size="xs"
                                variant="outline"
                                className="border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400"
                                onClick={() => reactivarCuenta(c.id)}
                              >
                                <RotateCcw /> Reactivar
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {editando?.clase === "empresa" ? (
        <EditarEmpresa empresa={empresa} alCerrar={() => setEditando(null)} />
      ) : null}
      {editando?.clase === "cuenta" ? (
        <EditarCuenta
          cuenta={editando.cuenta}
          empresaId={editando.empresaId}
          alCerrar={() => setEditando(null)}
        />
      ) : null}
    </Card>
  );
}

/**
 * Desde y hasta qué mes esta cuenta entregó estado de cuenta.
 *
 * NO SE EDITA (2026-09-09). Antes eran dos casillas y era el único campo
 * editable del maestro, lo que permitía que dijeran una cosa mientras los
 * archivos decían otra. Ahora se muestran: salen de los extractos confirmados
 * de la cuenta, que es un hecho comprobable.
 *
 * Y se avisa de los HUECOS. Que falte antes del primer mes o después del
 * último no es un problema —no se sabe cuándo debería haber empezado, ni si ya
 * se cerró—; el que importa es el mes salteado teniendo extractos a los dos
 * lados, porque ahí el saldo de cierre de uno no encadena con la apertura del
 * siguiente y nadie lo nota hasta que algo no cuadra meses después.
 */
function RangoDeEntrega({
  cuenta,
}: {
  cuenta: {
    entregaDesde: string | null;
    entregaHasta: string | null;
    meses: string[];
    huecos: string[];
  };
}) {
  if (!cuenta.entregaDesde)
    return (
      <span className="text-[11.5px] text-muted-foreground">
        todavía no entregó ninguno
      </span>
    );

  const uno = cuenta.entregaDesde === cuenta.entregaHasta;
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      <span className="text-[11.5px] tabular-nums">
        {uno
          ? cuenta.entregaDesde
          : `${cuenta.entregaDesde} → ${cuenta.entregaHasta}`}
      </span>
      <span className="text-[11px] text-muted-foreground">
        {cuenta.meses.length} {cuenta.meses.length === 1 ? "mes" : "meses"}
      </span>
      {cuenta.huecos.length > 0 ? (
        <span
          className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[11px] text-amber-700 dark:text-amber-500"
          title={`No hay extracto de: ${cuenta.huecos.join(", ")}`}
        >
          ⚠ falta {cuenta.huecos.join(", ")}
        </span>
      ) : null}
    </span>
  );
}
